/**
 * A customer's "I've paid" claim on the invoice detail page (design D,
 * pending claim card), plus the two dialogs every approval surface shares:
 * approve a different amount and reject with a reason. The Approvals screen
 * imports the dialogs from here so there is one implementation of each.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import InvoiceRepository, { Payment, RejectCode } from '../../../internals/repository/InvoiceRepository';
import { METHOD_LABEL, ago, date, money, moneyInput } from '../format';
import {
  REJECT_REASONS, REJECT_REASON_MAX, balanceAfter, checkDifferentAmount, checkReject, claimErrorText, rejectLabel, slipKind,
} from '../approvalsModel';
import { Dialog, ErrorBanner } from './VoidModal';

export interface ClaimRef {
  paymentId: string;
  amountMinor: number;
  possibleDuplicate?: boolean;
}

const labelCls = 'flex flex-col gap-1.5 text-[13px] font-semibold text-text2';
const errStyle = { color: 'var(--negFg)' };

/** Striped placeholder behind slips (and in place of a missing one). */
export const STRIPES = 'repeating-linear-gradient(135deg, var(--surface2) 0 6px, var(--neutralSoft) 6px 12px)';

export function DuplicateTag({ small }: { small?: boolean }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-md bg-neg-soft font-bold ${small ? 'h-[18px] px-[7px] text-[10.5px]' : 'h-5 px-2 text-[11px]'}`}
      style={{ color: 'var(--negFg)' }}
    >
      Possible duplicate
    </span>
  );
}

/** Approve less than the customer claimed: what actually reached the account. */
export function DifferentAmountDialog({
  shopId, claim, docNumber, balanceMinor, currency = 'LKR', onClose, onDone, onStale,
}: {
  shopId: string;
  claim: ClaimRef;
  docNumber?: string | null;
  balanceMinor: number;
  currency?: string;
  onClose: () => void;
  onDone: (approvedMinor: number) => void;
  /** The claim was decided elsewhere; the caller reloads. */
  onStale?: () => void;
}) {
  const [text, setText] = useState(moneyInput(Math.min(claim.amountMinor, Math.max(0, balanceMinor))));
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const check = checkDifferentAmount(text, claim.amountMinor, balanceMinor, currency);
  const after = check.error ? null : balanceAfter(balanceMinor, check.minor);

  const submit = async () => {
    setTouched(true);
    if (busy || check.error) return;
    setBusy(true);
    setError(null);
    try {
      await InvoiceRepository.approveClaim(shopId, claim.paymentId, check.minor);
      onDone(check.minor);
    } catch (e: any) {
      setError(claimErrorText(e));
      setBusy(false);
      if (e?.code === 'claim_not_pending') onStale?.();
    }
  };

  return (
    <Dialog
      title="Approve a different amount"
      sub={`The customer claimed ${money(claim.amountMinor, currency)} for ${docNumber ?? 'this invoice'}. Record what reached your account.`}
      onClose={onClose}
      busy={busy}
      footer={(
        <>
          <button type="button" className="nl-btn nl-btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="nl-btn nl-btn--primary" onClick={submit} disabled={busy}>
            {busy ? 'Approving…' : `Approve ${money(check.error ? 0 : check.minor, currency)}`}
          </button>
        </>
      )}
    >
      <form className="flex flex-col gap-3" noValidate onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <label className={labelCls}>
          Amount received
          <input
            className="nl-input nl-num"
            inputMode="decimal"
            value={text}
            aria-invalid={touched && !!check.error}
            style={touched && check.error ? { borderColor: 'var(--neg)' } : undefined}
            onChange={(e) => { setText(e.target.value); setError(null); }}
          />
          {touched && check.error && <span className="text-[12px] font-medium" style={errStyle}>! {check.error}</span>}
          {after !== null && (
            <span className="text-[12px] font-medium text-text3">
              Balance after approval: <span className="nl-num">{money(after, currency)}</span>. The customer is told the amount you approved.
            </span>
          )}
        </label>
        <ErrorBanner text={error} />
      </form>
    </Dialog>
  );
}

/** Reject a claim; the customer sees the reason and can send the claim again. */
export function RejectDialog({
  shopId, claim, docNumber, currency = 'LKR', onClose, onDone, onStale,
}: {
  shopId: string;
  claim: ClaimRef;
  docNumber?: string | null;
  currency?: string;
  onClose: () => void;
  onDone: (reason: string) => void;
  onStale?: () => void;
}) {
  const [code, setCode] = useState<RejectCode>(claim.possibleDuplicate ? 'DUPLICATE' : 'NOT_RECEIVED');
  const [other, setOther] = useState('');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const problem = checkReject(code, other);

  const submit = async () => {
    setTouched(true);
    if (busy || problem) return;
    setBusy(true);
    setError(null);
    try {
      const reason = code === 'OTHER' ? other.trim() : undefined;
      await InvoiceRepository.rejectClaim(shopId, claim.paymentId, code, reason);
      onDone(rejectLabel(code, reason));
    } catch (e: any) {
      setError(claimErrorText(e));
      setBusy(false);
      if (e?.code === 'claim_not_pending') onStale?.();
    }
  };

  return (
    <Dialog
      title="Reject payment claim"
      sub={`${money(claim.amountMinor, currency)} for ${docNumber ?? 'this invoice'}. The customer sees the reason and can send it again.`}
      onClose={onClose}
      busy={busy}
      footer={(
        <>
          <button type="button" className="nl-btn nl-btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="nl-btn nl-btn--danger" onClick={submit} disabled={busy}>
            {busy ? 'Rejecting…' : 'Reject claim'}
          </button>
        </>
      )}
    >
      <form className="flex flex-col gap-3" noValidate onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <fieldset className="m-0 flex flex-col gap-1.5 border-0 p-0">
          <legend className="mb-1.5 p-0 text-[13px] font-semibold text-text2">Reason</legend>
          {REJECT_REASONS.map((r) => {
            const on = r.code === code;
            return (
              <label
                key={r.code}
                className="flex cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2.5 text-[14px] font-medium text-text1"
                style={{ borderColor: on ? 'var(--brand)' : 'var(--border2)', background: on ? 'var(--brandSoft)' : 'var(--surface)' }}
              >
                <input
                  type="radio"
                  name="reject-reason"
                  value={r.code}
                  checked={on}
                  onChange={() => { setCode(r.code); setError(null); }}
                  style={{ accentColor: 'var(--brand)' }}
                />
                {r.label}
              </label>
            );
          })}
        </fieldset>
        {code === 'OTHER' && (
          <label className={labelCls}>
            Tell the customer why
            <input
              className="nl-input"
              value={other}
              maxLength={REJECT_REASON_MAX}
              placeholder="Short reason"
              aria-invalid={touched && !!problem}
              style={touched && problem ? { borderColor: 'var(--neg)' } : undefined}
              onChange={(e) => { setOther(e.target.value); setError(null); }}
            />
            {touched && problem && <span className="text-[12px] font-medium" style={errStyle}>! {problem}</span>}
            <span className="text-right text-[11px] font-normal text-text3">{other.length}/{REJECT_REASON_MAX}</span>
          </label>
        )}
        <ErrorBanner text={error} />
      </form>
    </Dialog>
  );
}

/** Small slip preview: the image, a PDF chip, or the striped placeholder. */
export function SlipThumb({ claim, width, height }: { claim: Pick<Payment, 'slipKey' | 'slipUrl'>; width: number; height: number }) {
  const kind = slipKind(claim);
  return (
    <span
      className="grid shrink-0 place-items-center overflow-hidden rounded-lg border border-borderc2"
      style={{ width, height, background: kind === 'image' ? 'var(--surface2)' : STRIPES }}
    >
      {kind === 'image' && <img src={claim.slipUrl} alt="" className="h-full w-full object-cover" />}
      {kind === 'pdf' && (
        <span className="rounded bg-surface px-1.5 py-0.5 font-mono text-[10px] font-bold text-text2">PDF</span>
      )}
      {kind === 'none' && <span className="font-mono text-[9px] text-text3">{height > 60 ? 'no slip' : ''}</span>}
    </span>
  );
}

export default function ClaimPanel({
  shopId, claim, balanceMinor, docNumber, currency = 'LKR', onDecided,
}: {
  shopId: string;
  claim: Payment;
  balanceMinor: number;
  docNumber?: string | null;
  currency?: string;
  /** Called after an approve or reject (or when the claim turned out to be decided already); the parent reloads. */
  onDecided: (message?: string) => void;
}) {
  const [modal, setModal] = useState<null | 'amount' | 'reject'>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const approve = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await InvoiceRepository.approveClaim(shopId, claim.paymentId);
      onDecided(`${money(claim.amountMinor, currency)} approved${res.receipt?.number ? ` · receipt ${res.receipt.number}` : ''}`);
    } catch (e: any) {
      setError(claimErrorText(e));
      if (e?.code === 'claim_not_pending') onDecided();
    } finally {
      setBusy(false);
    }
  };

  const reload = () => onDecided();
  const approvalsLink = `/invoices/approvals?claim=${encodeURIComponent(claim.paymentId)}`;

  return (
    <section
      className="flex flex-col gap-3 rounded-2xl bg-surface px-[18px] py-4"
      style={{ border: '1.5px solid var(--warn)' }}
      aria-label="Pending payment claim"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-bold uppercase tracking-[.08em]" style={{ color: 'var(--warnFg)' }}>Pending claim</span>
        {claim.possibleDuplicate && <DuplicateTag />}
      </div>

      <div className="grid items-center gap-3" style={{ gridTemplateColumns: '64px minmax(0,1fr)' }}>
        <Link to={approvalsLink} aria-label="View slip in Approvals" className="cursor-zoom-in rounded-lg">
          <SlipThumb claim={claim} width={64} height={80} />
        </Link>
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="nl-num text-[18px] font-semibold tabular-nums text-text1">{money(claim.amountMinor, currency)}</span>
          <span className="text-[13px] text-text2">{METHOD_LABEL[claim.method] ?? claim.method} · paid {date(claim.paidOn)}</span>
          <span className="truncate text-[12px] text-text3">
            Ref <span className="font-mono">{claim.reference || '—'}</span> · sent {ago(claim.createdAt)}
          </span>
        </div>
      </div>

      {claim.note && (
        <span className="break-words rounded-lg bg-surface2 px-2.5 py-2 text-[13px] text-text2">“{claim.note}”</span>
      )}

      <ErrorBanner text={error} />

      <div className="flex flex-wrap gap-2">
        <button type="button" className="nl-btn nl-btn--sm nl-btn--primary flex-1" disabled={busy} onClick={approve}>
          {busy ? 'Approving…' : 'Approve'}
        </button>
        <button type="button" className="nl-btn nl-btn--sm nl-btn--secondary" disabled={busy} onClick={() => setModal('amount')}>
          Different amount
        </button>
        <button
          type="button"
          className="nl-btn nl-btn--sm nl-btn--secondary"
          style={{ color: 'var(--negFg)' }}
          disabled={busy}
          onClick={() => setModal('reject')}
        >
          Reject
        </button>
      </div>

      {modal === 'amount' && (
        <DifferentAmountDialog
          shopId={shopId}
          claim={claim}
          docNumber={docNumber}
          balanceMinor={balanceMinor}
          currency={currency}
          onClose={() => setModal(null)}
          onStale={reload}
          onDone={(m) => { setModal(null); onDecided(`${money(m, currency)} approved`); }}
        />
      )}
      {modal === 'reject' && (
        <RejectDialog
          shopId={shopId}
          claim={claim}
          docNumber={docNumber}
          currency={currency}
          onClose={() => setModal(null)}
          onStale={reload}
          onDone={() => { setModal(null); onDecided('Claim rejected. The customer sees the reason on the invoice link.'); }}
        />
      )}
    </section>
  );
}
